import React, { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    FileText,
    CalendarClock,
    Heart,
    ClipboardCheck,
    TrendingUp,
    Activity
} from 'lucide-react';

// Import individual report components (to be created)
import { PatientClinicalSummaryReport } from '@/components/reports/clinical/PatientClinicalSummaryReport';
import { PatientTimelineReport } from '@/components/reports/clinical/PatientTimelineReport';
import { MaternalRiskAnalysisReport } from '@/components/reports/clinical/MaternalRiskAnalysisReport';
import { VonCompletenessReport } from '@/components/reports/clinical/VonCompletenessReport';
import { OutcomesAnalysisReport } from '@/components/reports/clinical/OutcomesAnalysisReport';
import { ProcedureOutcomesReport } from '@/components/reports/clinical/ProcedureOutcomesReport';

export const ClinicalReportsPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState('patient-summary');

    const reportCards = [
        {
            id: 'patient-summary',
            title: 'Rezumat clinic pacient',
            description: 'Profil complet pacient cu cronologie completă',
            icon: FileText,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100',
        },
        {
            id: 'timeline',
            title: 'Cronologie diagnostic și tratament',
            description: 'Evoluția pacientului în timp',
            icon: CalendarClock,
            color: 'text-purple-600',
            bgColor: 'bg-purple-100',
        },
        {
            id: 'maternal-risk',
            title: 'Factori de risc matern',
            description: 'Analiza condițiilor materne și corelații',
            icon: Heart,
            color: 'text-pink-600',
            bgColor: 'bg-pink-100',
        },
        {
            id: 'von-completeness',
            title: 'Completitudine date RONN',
            description: 'Status completare date RONN',
            icon: ClipboardCheck,
            color: 'text-green-600',
            bgColor: 'bg-green-100',
        },
        {
            id: 'outcomes',
            title: 'Rezultate la 30 de zile',
            description: 'Analiză supraviețuire și complicații',
            icon: TrendingUp,
            color: 'text-orange-600',
            bgColor: 'bg-orange-100',
        },
        {
            id: 'procedure-outcomes',
            title: 'Rezultate proceduri',
            description: 'Rate de succes și reintervenții',
            icon: Activity,
            color: 'text-red-600',
            bgColor: 'bg-red-100',
        },
    ];

    return (
        <MainLayout>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Rapoarte clinice</h1>
                    <p className="text-muted-foreground mt-2">
                        Rapoarte detaliate focusate pe îngrijirea individuală a pacientului și rezultate clinice
                    </p>
                </div>

                {/* Report Cards Grid */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {reportCards.map((report) => {
                        const Icon = report.icon;
                        return (
                            <Card
                                key={report.id}
                                className={`cursor-pointer hover:shadow-lg transition-all ${
    activeTab === report.id ? 'ring-2 ring-primary' : ''
}`}
                                onClick={() => setActiveTab(report.id)}
                            >
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        {report.title}
                                    </CardTitle>
                                    <div className={`rounded-full p-2 ${report.bgColor}`}>
                                        <Icon className={`h-4 w-4 ${report.color}`} />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-xs text-muted-foreground">
                                        {report.description}
                                    </p>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Report Content */}
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {reportCards.find((r) => r.id === activeTab)?.title}
                        </CardTitle>
                        <CardDescription>
                            {reportCards.find((r) => r.id === activeTab)?.description}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                            <TabsList className="hidden">
                                {reportCards.map((report) => (
                                    <TabsTrigger key={report.id} value={report.id}>
                                        {report.title}
                                    </TabsTrigger>
                                ))}
                            </TabsList>

                            <TabsContent value="patient-summary" className="mt-0">
                                <PatientClinicalSummaryReport />
                            </TabsContent>

                            <TabsContent value="timeline" className="mt-0">
                                <PatientTimelineReport />
                            </TabsContent>

                            <TabsContent value="maternal-risk" className="mt-0">
                                <MaternalRiskAnalysisReport />
                            </TabsContent>

                            <TabsContent value="von-completeness" className="mt-0">
                                <VonCompletenessReport />
                            </TabsContent>

                            <TabsContent value="outcomes" className="mt-0">
                                <OutcomesAnalysisReport />
                            </TabsContent>

                            <TabsContent value="procedure-outcomes" className="mt-0">
                                <ProcedureOutcomesReport />
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>
            </div>
        </MainLayout>
    );
};
